import { ChangeDetectionStrategy, Component, computed, effect, inject, input, Renderer2, signal } from '@angular/core';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { IHtmlBlok } from './html.model';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-html',
    imports: [],
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block',
        '[class]': 'getAllClasses()',
        '[innerHTML]': 'html()',
    },
})
export class HtmlComponent {
    static nextId = 0;
    readonly id = `html-component-${HtmlComponent.nextId++}`;
    private _tailwind = inject(TailwindService);
    private _renderer = inject(Renderer2);
    private _document = inject(DOCUMENT);
    private _sanitizer = inject(DomSanitizer);

    block = input.required<IHtmlBlok>();

    html = signal<SafeHtml | null>(null);

    getAllClasses = computed<string[]>(() => {
        return this._tailwind.getDisplayClasses(this.block());
    });

    constructor() {
        effect(() => {
            const block = this.block();
            if (block.cssText) {
                this._appendStyles(block.cssText);
            } else {
                this._removeStyles();
            }
            if (block.jsText) {
                this._appendScripts(block.jsText);
            } else {
                this._removeScripts();
            }
            if (block.htmlText) {
                this.html.set(this._sanitizer.bypassSecurityTrustHtml(block.htmlText));
            } else {
                this.html.set(null);
            }
        });
    }

    private _removeStyles(): void {
        const oldStyle = this._document.getElementById(this.id + '-style');
        if (oldStyle) {
            this._renderer.removeChild(this._document.head, oldStyle);
        }
    }

    private _appendStyles(css: string) {
        const style = this._renderer.createElement('style');
        style.id = this.id + '-style';
        style.innerHTML = css;
        this._removeStyles();
        this._renderer.appendChild(this._document.head, style);
    }

    private _removeScripts(): void {
        const oldScript = this._document.getElementById(this.id + '-script');
        if (oldScript) {
            this._renderer.removeChild(this._document.body, oldScript);
        }
    }

    private _appendScripts(js: string) {
        const script = this._renderer.createElement(`script`);
        script.id = this.id + '-script';
        script.innerHTML = js;
        this._removeScripts();
        this._renderer.appendChild(this._document.body, script);
    }
}
